import { AgentsService } from '@inference.engine/workspace-api-client/web';
import { z } from 'zod';
import { workspaceHttp } from '@/lib/api/workspace-client';
import type { UploadedFileAsset } from '../model/upload.store.types';

const PROJECT_INTAKE_EXTRACTION_AGENT_ID = 'project-intake-extraction-v1';

/**
 * The run route refuses a request carrying more than this (`MAX_ATTACHMENTS` in agentic-nestjs),
 * and nothing upstream caps the upload count. Sending the first N prefills a form the user can
 * still edit; sending all of them 400s the request and leaves the form empty.
 */
const MAX_ANALYZED_DOCUMENTS = 25;

const projectPrefillSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  type: z
    .enum([
      'brand_strategy',
      'campaigns',
      'creative_production',
      'content_marketing',
      'events',
      'experiential_marketing',
    ])
    .nullable(),
  market: z.enum(['APAC', 'ANZ', 'LATAM', 'EMEA', 'North America', 'Worldwide']).nullable(),
});

export type ProjectPrefill = z.infer<typeof projectPrefillSchema>;

export async function prefillProjectFromDocuments(
  files: UploadedFileAsset[],
  signal?: AbortSignal,
): Promise<ProjectPrefill | null> {
  const response = await AgentsService.run({
    client: workspaceHttp,
    path: { agentId: PROJECT_INTAKE_EXTRACTION_AGENT_ID },
    body: {
      input: {
        // Kept in step with PROJECT_INTAKE_EXTRACTION_PROMPT in workspace-api, which is what the
        // agent's evals score. This bundle cannot import from a service, so the copy is manual.
        prompt:
          // eslint-disable-next-line i18next/no-literal-string -- prompt sent to the model
          'Extract the project title, description, type, and market from the attached document(s).',
      },
      // References, not bytes: workspace-api resolves each id against assets-api.
      attachments: files.slice(0, MAX_ANALYZED_DOCUMENTS).map((file) => ({
        mediaType: file.mimeType,
        // eslint-disable-next-line i18next/no-literal-string -- names the host resolver, not copy
        kind: 'asset',
        id: file.assetId,
        filename: file.fileName,
      })),
    },
    signal,
    throwOnError: true,
  });

  const parsed = projectPrefillSchema.safeParse(response.data.output);
  return parsed.success ? parsed.data : null;
}
